import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ProgressBar } from "./ProgressBar";
import { AnimatedNumber } from "./AnimatedNumber";
import {
  acceptsCoin,
  computeSpend,
  effectivePrice,
  feeForOption,
  feeIsSpiked,
  formatFee,
  formatUsd,
  resolveDecision,
  scenarioFlow,
} from "../engine";
import type {
  DayMarket,
  GameMode,
  RunResult,
  Scenario,
  ScenarioFlow,
  ScenarioOption,
} from "../types";

interface ScenarioScreenProps {
  scenario: Scenario;
  mode: GameMode;
  run: RunResult;
  markets: DayMarket[];
  priceStatus: "loading" | "live" | "fallback";
  progress: {
    day: number;
    stepInDay: number;
    stepsInDay: number;
  };
  onChoose: (option: ScenarioOption) => void;
}

export function ScenarioScreen({
  scenario,
  mode,
  run,
  markets,
  priceStatus,
  progress,
  onChoose,
}: ScenarioScreenProps) {
  const [revealed, setRevealed] = useState<ScenarioOption | null>(null);

  const market = markets.find((m) => m.day === scenario.day) ?? markets[0];
  const coinLabel = mode;
  const flow = scenarioFlow(scenario);
  const isEarn = flow === "earn";

  function pick(option: ScenarioOption) {
    setRevealed(option);
  }

  function proceed() {
    if (revealed) {
      onChoose(revealed);
      setRevealed(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <ProgressBar
        currentDay={progress.day}
        stepInDay={progress.stepInDay}
        stepsInDay={progress.stepsInDay}
      />

      {/* Wallet + market strip */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className={`rounded-lg px-2 py-2 ${run.remainingFiatUsd < 5 ? "bg-destructive/10" : "bg-muted/70"}`}>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Fiat</p>
          <p className={`text-sm font-bold ${run.remainingFiatUsd < 5 ? "text-destructive" : "text-foreground"}`}>
            <AnimatedNumber value={run.remainingFiatUsd} format={formatUsd} />
          </p>
        </div>
        <div className={`rounded-lg px-2 py-2 ${run.remainingCryptoUsd < 5 ? "bg-destructive/10" : "bg-primary/10"}`}>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{coinLabel}</p>
          <p className={`text-sm font-bold ${run.remainingCryptoUsd < 5 ? "text-destructive" : "text-primary"}`}>
            <AnimatedNumber value={run.remainingCryptoUsd} format={formatUsd} />
          </p>
        </div>
        <div className="rounded-lg bg-muted/70 px-2 py-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Mood</p>
          <p className="text-sm font-bold text-foreground">
            <AnimatedNumber value={run.wellbeing} format={(n) => `${Math.round(n)}%`} />
          </p>
        </div>
      </div>

      <p className="text-[11px] text-center text-muted-foreground">
        {priceStatus === "live" ? "Live" : "Sim."} rate: 1 {coinLabel} ={" "}
        {formatUsd(market.priceUsd[mode])}
        {market.day > 1 ? " (sim. drift)" : ""} · {market.headline}
      </p>

      {/* Network conditions — highlights BTC congestion for BTC players */}
      {market.networkNote && (
        <div
          className={`text-[11px] text-center rounded-lg px-3 py-2 ${
            mode === "BTC" && market.btcCongested
              ? "bg-destructive/10 text-destructive font-medium"
              : "bg-muted/60 text-muted-foreground"
          }`}
        >
          {mode === "BTC" && market.btcCongested
            ? market.networkNote
            : `Network: BTC on-chain fee ~${formatFee(market.btcOnchainFeeUsd)} today · BCH flat at ${formatFee(0.004)}`}
        </div>
      )}

      {/* Scenario card — re-animates each time a new scenario appears */}
      <div key={`${scenario.day}-${scenario.id}`} className="flex flex-col gap-4 animate-screen-enter">
      <Card className={`overflow-hidden ${isEarn ? "border-primary/40" : ""}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-4xl" aria-hidden>{scenario.icon}</div>
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Badge variant="secondary">{scenario.category}</Badge>
                <Badge
                  className={`text-[10px] ${
                    isEarn
                      ? "bg-primary/15 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {isEarn ? "↑ Income" : "↓ Spend"}
                </Badge>
              </div>
              <h2 className="text-xl font-bold leading-tight text-foreground">{scenario.title}</h2>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">{scenario.description}</p>
        </CardContent>
      </Card>

      {/* Options or outcome */}
      {!revealed ? (
        <div className="flex flex-col gap-2.5">
          {scenario.options.map((option) => (
            <OptionButton
              key={option.id}
              option={option}
              mode={mode}
              day={scenario.day}
              flow={flow}
              onClick={() => pick(option)}
            />
          ))}
        </div>
      ) : (
        <OutcomeCard scenario={scenario} option={revealed} mode={mode} run={run} onNext={proceed} />
      )}
      </div>
    </div>
  );
}

function OptionButton({
  option,
  mode,
  day,
  flow,
  onClick,
}: {
  option: ScenarioOption;
  mode: GameMode;
  day: number;
  flow: ScenarioFlow;
  onClick: () => void;
}) {
  const btnClass =
    "w-full text-left rounded-xl border-2 border-border bg-card px-4 py-3 transition-all hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]";

  if (flow === "earn") {
    return <EarnOptionButton option={option} mode={mode} day={day} onClick={onClick} className={btnClass} />;
  }

  const isCrypto = option.method === "crypto";
  const accepted = acceptsCoin(option, mode);
  const price = effectivePrice(option, mode);
  const fee = isCrypto && accepted && option.priceUsd > 0 ? feeForOption(option, mode, day) : 0;
  const spiked = isCrypto && accepted && feeIsSpiked(option, mode, day) && fee >= 5;
  const hasDiscount = isCrypto && accepted && !!option.cryptoDiscount;

  return (
    <button onClick={onClick} className={btnClass}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-foreground">
              {isCrypto ? `Pay with ${mode}` : option.label}
            </span>
            {option.skip && <Badge variant="outline" className="text-[10px]">skip</Badge>}
            {hasDiscount && (
              <Badge className="text-[10px] bg-accent text-accent-foreground">
                -{Math.round((option.cryptoDiscount ?? 0) * 100)}%
              </Badge>
            )}
            {isCrypto && !accepted && (
              <Badge variant="destructive" className="text-[10px]">not accepted</Badge>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${spiked ? "text-destructive font-medium" : "text-muted-foreground"}`}>
            {fee > 0
              ? `+ ${formatFee(fee)} sim. network fee${spiked ? " ⚠️ spike!" : ""}`
              : "no crypto fee"}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-bold text-foreground">{formatUsd(price)}</p>
          {hasDiscount && (
            <p className="text-[11px] text-muted-foreground line-through">
              {formatUsd(option.priceUsd)}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function EarnOptionButton({
  option,
  mode,
  day,
  onClick,
  className,
}: {
  option: ScenarioOption;
  mode: GameMode;
  day: number;
  onClick: () => void;
  className: string;
}) {
  const isCrypto = option.method === "crypto";
  const accepted = acceptsCoin(option, mode);
  const gross = option.priceUsd;

  // Declining income.
  if (option.skip) {
    return (
      <button onClick={onClick} className={className}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="font-semibold text-foreground">{option.label}</span>
            <Badge variant="outline" className="text-[10px]">decline</Badge>
          </div>
          <p className="font-bold text-muted-foreground shrink-0">+$0.00</p>
        </div>
      </button>
    );
  }

  const usingCrypto = isCrypto && accepted;
  const fee = usingCrypto && gross > 0 ? feeForOption(option, mode, day) : 0;
  const spiked = usingCrypto && feeIsSpiked(option, mode, day) && fee >= 5;
  const cut = !usingCrypto && option.fiatCut ? option.fiatCut : 0;
  const lost = cut ? Math.round(gross * cut * 100) / 100 : 0;
  const net = usingCrypto
    ? Math.round((gross - fee) * 100) / 100
    : Math.round((gross - lost) * 100) / 100;

  return (
    <button onClick={onClick} className={className}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-foreground">
              {isCrypto ? `Receive in ${mode}` : option.label}
            </span>
            {cut > 0 && (
              <Badge variant="destructive" className="text-[10px]">
                -{Math.round(cut * 100)}% cut
              </Badge>
            )}
            {isCrypto && !accepted && (
              <Badge variant="destructive" className="text-[10px]">not supported</Badge>
            )}
            {option.settleDays && option.settleDays >= 2 && (
              <Badge variant="outline" className="text-[10px]">~{option.settleDays}d wait</Badge>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${spiked ? "text-destructive font-medium" : "text-muted-foreground"}`}>
            {usingCrypto
              ? fee > 0
                ? `− ${formatFee(fee)} sim. receive fee${spiked ? " ⚠️ spike!" : ""}`
                : "instant, no fee"
              : lost > 0
                ? `− ${formatUsd(lost)} service cut`
                : "no cut"}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-bold text-primary">+{formatUsd(net)}</p>
          {net < gross && (
            <p className="text-[11px] text-muted-foreground line-through">
              {formatUsd(gross)}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function OutcomeCard({
  scenario,
  option,
  mode,
  run,
  onNext,
}: {
  scenario: Scenario;
  option: ScenarioOption;
  mode: GameMode;
  run: RunResult;
  onNext: () => void;
}) {
  const accepted = acceptsCoin(option, mode);
  const failed = option.method === "crypto" && !accepted;

  // Use the engine's authoritative resolution so the reveal text always
  // matches what actually happens to the player's balances.
  const decision = resolveDecision(scenario, option, mode);

  // Preview how this purchase draws from the wallets, using the SAME logic the
  // engine will apply, so the message matches the balances after Continue.
  const spend =
    decision.flow === "spend" ? decision.costUsd + decision.feeUsd : 0;
  const walletMethod = failed ? "fiat" : decision.method;
  const spendResult =
    decision.flow === "spend"
      ? computeSpend(run.remainingFiatUsd, run.remainingCryptoUsd, spend, walletMethod)
      : null;
  const dipped = spendResult?.dippedIntoOtherUsd ?? 0;
  const shortfall = spendResult?.shortfallUsd ?? 0;

  const good = option.wellbeing >= 0 && !failed && shortfall === 0;

  // A spiked network fee is a dramatic "bad" moment worth reacting to.
  const spiked =
    option.method === "crypto" && accepted && feeIsSpiked(option, mode, scenario.day) && decision.feeUsd >= 5;
  const alarming = failed || spiked || shortfall > 0;

  const chosenLabel = decision.method === "crypto" ? mode : "cash";
  const otherLabel = decision.method === "crypto" ? "cash" : mode;

  return (
    <>
      {alarming && (
        <div
          className="pointer-events-none fixed inset-0 z-40 bg-destructive animate-danger-flash"
          aria-hidden
        />
      )}
      <Card
        className={`animate-pop-in ${alarming ? "animate-danger-shake " : ""}${
          failed ? "border-destructive/50" : good ? "border-primary/50" : "border-border"
        }`}
      >
        <CardContent className="p-5 flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="text-3xl" aria-hidden>
              {shortfall > 0 ? "💸" : failed ? "⚠️" : good ? "✅" : "😕"}
            </div>
            <p className="text-sm text-foreground leading-relaxed">{decision.outcome}</p>
          </div>

          {/* Wallet consequences: dipping into the other wallet, or coming up short. */}
          {shortfall > 0 ? (
            <div className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive font-medium leading-relaxed">
              You didn't have enough to cover the full {formatUsd(spend)}. You came up{" "}
              {formatUsd(shortfall)} short and had to go without — your mood took a hit.
            </div>
          ) : dipped > 0 ? (
            <div className="rounded-lg bg-muted/70 px-3 py-2 text-xs text-muted-foreground leading-relaxed">
              Your {chosenLabel} balance ran low, so {formatUsd(dipped)} came out of your{" "}
              {otherLabel} instead.
            </div>
          ) : null}

          <Button className="w-full h-12 font-bold" onClick={onNext}>
            Continue
          </Button>
        </CardContent>
      </Card>
    </>
  );
}
